import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { PageHeader, EmptyState } from '@/components/Shared';
import { DangerConfirm } from '@/components/DangerConfirm';
import { money } from '@/lib/format';
import { Users2, Plus, Trash2, Star, Pencil, Briefcase, Power } from 'lucide-react';
import { toast } from 'sonner';

export function BalanceBadge({ status }) {
  const map = {
    settled: { cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', label: 'Paid / Settled' },
    partial: { cls: 'bg-blue-50 text-blue-700 border-blue-200', label: 'Partially Paid' },
    unpaid: { cls: 'bg-amber-50 text-amber-800 border-amber-200', label: 'Balance Due' },
  };
  const s = map[status] || map.settled;
  return <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${s.cls}`}>{s.label}</span>;
}

export default function Customers() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', phone: '', address: '', discount_percent: 0 });
  const [delTarget, setDelTarget] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => { const { data } = await api.get('/customers'); setCustomers(data); };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm({ name: '', email: '', phone: '', address: '', discount_percent: 0 }); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ name: c.name, email: c.email || '', phone: c.phone || '', address: c.address || '', discount_percent: c.discount_percent || 0 }); setOpen(true); };

  const save = async () => {
    if (!form.name) { toast.error('Name required'); return; }
    const payload = { ...form, discount_percent: Math.max(0, Math.min(50, Number(form.discount_percent) || 0)) };
    try {
      if (editing) { await api.put(`/customers/${editing.id}`, payload); toast.success('Customer updated'); }
      else { await api.post('/customers', payload); toast.success('Customer added'); }
      setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const toggleActive = async (e, c) => {
    e.stopPropagation();
    try { await api.put(`/customers/${c.id}`, { active: c.active === false }); toast.success(c.active === false ? 'Customer activated' : 'Customer deactivated'); load(); }
    catch (er) { toast.error(er?.response?.data?.detail || 'Failed'); }
  };
  const doDelete = async (password) => {
    setBusy(true);
    try {
      await api.post(`/customers/${delTarget.id}/delete`, { password, confirm: 'CONFIRM' });
      toast.success(`${delTarget.name} deleted`);
      setDelTarget(null); load();
    } catch (er) { toast.error(er?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  const edit = (e, c) => { e.stopPropagation(); openEdit(c); };

  return (
    <div>
      <PageHeader title="Customers" subtitle="Employees are also customers. Click a row to view balance & invoices." testid="customers-header"
        actions={<Button onClick={openNew} data-testid="customers-add-button"><Plus className="h-4 w-4 mr-2" /> Add Customer</Button>} />
      <Card className="p-0 overflow-hidden">
        {customers.length === 0 ? <div className="p-6"><EmptyState icon={Users2} title="No customers" /></div> : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Customer</TableHead><TableHead>Type</TableHead><TableHead className="text-right">Discount %</TableHead>
                <TableHead className="text-right">Total Sales</TableHead><TableHead className="text-right">Paid</TableHead>
                <TableHead className="text-right">Balance Due</TableHead><TableHead>Status</TableHead><TableHead></TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {customers.map((c) => (
                  <TableRow key={c.id} className="cursor-pointer" data-testid="customer-row" onClick={() => navigate(`/customers/${c.id}`)}>
                    <TableCell className="font-medium flex items-center gap-1.5">{c.name}{c.is_default && <Star className="h-3.5 w-3.5 text-amber-500 fill-amber-500" title="Default" />}{c.active === false && <span className="inline-flex items-center rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-[10px] font-medium text-red-700" data-testid="customer-inactive-badge">Inactive</span>}</TableCell>
                    <TableCell>
                      {c.type === 'employee'
                        ? <span className="inline-flex items-center gap-1 rounded-full border border-teal-200 bg-teal-50 px-2.5 py-0.5 text-xs font-medium text-teal-800"><Briefcase className="h-3 w-3" /> Employee</span>
                        : <span className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium bg-slate-100 text-slate-700">Regular</span>}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{(c.discount_percent || 0)}%</TableCell>
                    <TableCell className="text-right tabular-nums">{money(c.total_invoiced)}</TableCell>
                    <TableCell className="text-right tabular-nums">{money(c.total_paid)}</TableCell>
                    <TableCell className="text-right tabular-nums font-semibold">{money(c.balance_due)}</TableCell>
                    <TableCell><BalanceBadge status={c.balance_status} /></TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <div className="flex gap-1 justify-end">
                        <Button variant="ghost" size="icon" onClick={(e) => edit(e, c)} data-testid="customer-edit-button"><Pencil className="h-4 w-4" /></Button>
                        {!c.is_default && <Button variant="ghost" size="icon" onClick={(e) => toggleActive(e, c)} title={c.active === false ? 'Activate' : 'Deactivate'} data-testid="customer-deactivate-button"><Power className={`h-4 w-4 ${c.active === false ? 'text-emerald-600' : 'text-amber-600'}`} /></Button>}
                        {!c.is_default && <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); setDelTarget(c); }} data-testid="customer-delete-button"><Trash2 className="h-4 w-4 text-red-600" /></Button>}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading">{editing ? 'Edit Customer' : 'Add Customer'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="customer-name-input" className="mt-1.5" /></div>
            <div><Label>Discount % (0–50)</Label><Input type="number" min="0" max="50" step="0.5" value={form.discount_percent} onChange={(e) => setForm({ ...form, discount_percent: e.target.value })} data-testid="customer-discount-input" className="mt-1.5" /></div>
            <div><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" /></div>
            <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5" /></div>
            <div><Label>Address</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-1.5" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} data-testid="customer-save-button">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <DangerConfirm
        open={!!delTarget}
        onOpenChange={(o) => !o && setDelTarget(null)}
        title={`Delete ${delTarget?.name || 'customer'}?`}
        description="This permanently deletes the customer along with their invoices and payments. Balances and reports update automatically. This cannot be undone."
        affectedText={delTarget ? `Deletes ${delTarget.name} — ${delTarget.total_invoices || 0} invoice(s) and their payments. Current balance ${money(delTarget.balance_due)}.` : ''}
        onConfirm={doDelete}
        busy={busy}
        confirmLabel="Delete Customer"
      />
    </div>
  );
}
